import type { DashboardTrainingSession } from "../../../../hooks/use-training-sessions.ts";

export type SessionIconKey = "graduation" | "coffee" | "megaphone" | "users";

export const CELEBRATION_BANNER_SESSION_SLIDE_TARGET = 5;

export type SessionBannerSlide = {
  session: DashboardTrainingSession;
  isUpcoming: boolean;
};

export function sessionEventTypeToIconKey(eventType: string): SessionIconKey {
  if (eventType === "Classroom Session") return "graduation";
  if (eventType === "Coffee and Learn Sessions") return "coffee";
  if (eventType === "Salesforce Sessions") return "megaphone";
  if (eventType === "Mulesoft Meetups") return "users";
  return "graduation";
}

function bannerEventTypePhrase(eventType: string): string {
  if (eventType.endsWith(" Sessions")) {
    return `${eventType.slice(0, -" Sessions".length)} Session`;
  }
  return eventType;
}

export function formatSessionBannerPrimaryLine(
  headline: string,
  eventType: string | undefined,
): string {
  const raw = eventType?.trim();
  if (!raw) return headline;
  const t = bannerEventTypePhrase(raw);

  switch (headline) {
    case "Today's session":
      return `Today's ${t}`;
    case "Upcoming session":
      return `Upcoming ${t}`;
    case "Starting soon":
      return `Starting soon · ${t}`;
    case "Session in progress":
      return `${t} in progress`;
    default:
      return `${headline} · ${raw}`;
  }
}

function parseStart(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d : null;
}

function parseEnd(endIso: string | undefined, start: Date): Date {
  if (endIso) {
    const e = new Date(endIso);
    if (Number.isFinite(e.getTime())) return e;
  }
  return start;
}

function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function getSessionsForCelebrationBanner(
  sessions: DashboardTrainingSession[] | undefined,
  now: Date,
): { slides: SessionBannerSlide[] } {
  const list = sessions ?? [];
  const withStart = list
    .map((s) => ({ s, t: parseStart(s.startDateTime) }))
    .filter((x): x is { s: DashboardTrainingSession; t: Date } => x.t != null)
    .sort((a, b) => a.t.getTime() - b.t.getTime());

  const todayItems = withStart
    .filter(({ t }) => sameLocalDay(t, now))
    .map(({ s }) => s);

  const todayIds = new Set(todayItems.map((s) => s.id));

  if (todayItems.length >= CELEBRATION_BANNER_SESSION_SLIDE_TARGET) {
    return {
      slides: todayItems.map((session) => ({ session, isUpcoming: false })),
    };
  }

  const need = CELEBRATION_BANNER_SESSION_SLIDE_TARGET - todayItems.length;
  const upcomingItems: DashboardTrainingSession[] = [];

  for (const { s, t } of withStart) {
    if (todayIds.has(s.id)) continue;
    if (t.getTime() <= now.getTime()) continue;
    upcomingItems.push(s);
    if (upcomingItems.length >= need) break;
  }

  return {
    slides: [
      ...todayItems.map((session) => ({ session, isUpcoming: false })),
      ...upcomingItems.map((session) => ({ session, isUpcoming: true })),
    ],
  };
}

export function getSessionBannerHeadline(
  session: DashboardTrainingSession,
  now: Date,
  isUpcoming: boolean,
): string {
  if (isUpcoming) return "Upcoming session";
  const start = parseStart(session.startDateTime);
  if (!start) return "Today's session";
  const end = parseEnd(session.endDateTime, start);
  if (now >= start && now < end) return "Session in progress";
  if (sameLocalDay(start, now) && now < start) {
    const ms = start.getTime() - now.getTime();
    if (ms >= 0 && ms <= 60 * 60 * 1000) return "Starting soon";
  }
  return "Today's session";
}
